import React from 'react';
import { useProfile } from '../context/ProfileContext';
import { useSession } from '../context/SessionContext';
import InitialAvatar from './InitialAvatar';
import './comic.css';

/**
 * Persistent row of "jump to this Hero" pills, shown as soon as ANY Hero on
 * the account has a live Mission -- not just pills for Heroes already
 * mid-workout. Once a workout starts, the whole point is making it just as
 * fast to switch to an IDLE Hero and start a second, independent workout
 * under them (per direct user request) as it is to jump back to one
 * already in progress -- so every non-archived profile gets a pill here,
 * each marked "Live" only if it actually has a session. Tapping any pill
 * focuses that Hero (selectProfile) and jumps to the "Select Mission" tab,
 * which shows either their in-progress Mission (if they have one) or the
 * routine list to start a new one (if they don't) -- RoutinePicker.jsx
 * keeps one ActiveMission mounted per profile with a live session (hidden
 * via CSS unless it's the focused one), so jumping here never loses
 * whichever Hero's in-progress, not-yet-logged state. Renders nothing
 * while no session is active anywhere on the account -- this is an
 * in-workout convenience, not a general-purpose Hero switcher (that's
 * still ProfileSwitcher's job).
 */
const ActiveSessionsBar = ({ onJumpToSession }) => {
  const { profiles, activeProfile, selectProfile } = useProfile();
  const { sessionsByProfile } = useSession();

  const hasAnySession = Object.keys(sessionsByProfile).length > 0;
  const visibleProfiles = profiles.filter((p) => p.isActive !== false);
  if (!hasAnySession || visibleProfiles.length === 0) return null;

  const handleJump = (profileId) => {
    if (profileId !== activeProfile?.id) selectProfile(profileId);
    onJumpToSession();
  };

  return (
    <div className="comic-panel" style={{ padding: "8px 10px", margin: "0 0 10px 0" }} data-tour="active-sessions-bar">
      {/* "Who's Training" per direct user choice: reads naturally for both
          the "Live" pills and the idle ones shown alongside them (an idle
          Hero isn't mid-Mission, but tapping their pill is how they start
          training too), unlike "Current Missions," which would only have
          been accurate for the live half of the row.
          [2026-10-17] Switched from a small monospace caption (matching
          RoutinePicker.jsx's muscle sub-headers) to the shared `.comic-title`
          class -- the same cursive title font every panel heading (e.g.
          "Select Mission") already uses -- per direct user request, dropping
          the caption-style bold/uppercase/dim-opacity treatment along with
          it so this reads as a title, not a dimmed label. `marginLeft: 149px`
          is an empirically-measured offset (Playwright bounding-box check)
          that lines this label's left edge up with "Select Mission"'s own
          title text one panel below -- that panel's title sits to the right
          of its 144px mascot box + 14px row gap, which this bar has no
          mascot of its own to reproduce structurally, so the same visual
          starting position is replicated directly as a margin instead. */}
      <div className="comic-title" style={{ fontSize: "18px", margin: "0 0 6px 149px" }}>
        Who's Training
      </div>
      <div style={{ display: "flex", gap: "8px", overflowX: "auto", alignItems: "center" }}>
      {visibleProfiles.map((profile) => {
        const isFocused = profile.id === activeProfile?.id;
        const isLive = !!sessionsByProfile[profile.id];
        return (
          <button
            key={profile.id}
            type="button"
            onClick={() => handleJump(profile.id)}
            data-tour-profile={profile.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexShrink: 0,
              background: "none",
              border: isFocused ? "2px solid #00a274" : "2px solid transparent",
              borderRadius: "20px",
              padding: "3px 10px 3px 3px",
              cursor: "pointer"
            }}
          >
            <InitialAvatar name={profile.name} photoURL={profile.photoURL} size={26} />
            <span style={{ fontFamily: "'Anonymous Pro', monospace", fontSize: "13px", fontWeight: "bold", color: "#232526" }}>
              {profile.name}
            </span>
            {isLive && (
              <span className="comic-eyebrow" style={{ fontSize: "10px", padding: "1px 6px" }}>
                Live
              </span>
            )}
          </button>
        );
      })}
      </div>
    </div>
  );
};

export default ActiveSessionsBar;
